import { useCallback, useState } from "react";
import Modal from "./Modal";
import { useGesture } from "../hooks/useGesture";
import type { StyleConfig } from "../state/styles";
import type { Character, Word } from "../state/types";

const CHARACTER_ID_ATTRIBUTE = "data-character-id";

interface WordEditorProps {
  word: Word;
  close: () => void;
  /** Called with the edited characters when the reader is done. */
  save: (id: string, characters: Character[]) => void;
  textStyle: StyleConfig["text"];
}

/**
 * Lets a reader erase individual characters, carving a shorter word out of a
 * longer one. Edits are local until the dialog closes, so the reader can
 * experiment without filling the undo history with intermediate states.
 */
const WordEditor = ({ word, close, save, textStyle }: WordEditorProps) => {
  const [characters, setCharacters] = useState<Character[]>(word.characters);

  const isMarked = useCallback(
    (id: string) => characters.find((character) => character.id === id)?.isMarked,
    [characters],
  );

  const onPaint = useCallback((ids: string[], paint: boolean) => {
    const painted = new Set(ids);
    setCharacters((previous) =>
      previous.map((character) =>
        painted.has(character.id) ? { ...character, isMarked: paint } : character,
      ),
    );
  }, []);

  const { handlers } = useGesture({ onPaint, isMarked }, CHARACTER_ID_ATTRIBUTE);

  const handleClose = useCallback(() => {
    const changed = characters.some(
      (character, index) => character.isMarked !== word.characters[index]?.isMarked,
    );
    if (changed) save(word.id, characters);
    close();
  }, [characters, close, save, word.characters, word.id]);

  const toggle = (id: string) =>
    setCharacters((previous) =>
      previous.map((character) =>
        character.id === id ? { ...character, isMarked: !character.isMarked } : character,
      ),
    );

  return (
    <Modal
      close={handleClose}
      label={`Edit characters of "${word.characters.map((c) => c.character).join("")}"`}
    >
      <div
        {...handlers}
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "8px",
          // A long word's letters can outrun the dialog, which scrolls; the gaps
          // between them pan it, exactly as the page's gutters pan the article.
          touchAction: "pan-y",
        }}
      >
        {characters.map(({ character, id, isMarked: marked }) => (
          <button
            key={id}
            type="button"
            {...{ [CHARACTER_ID_ATTRIBUTE]: id }}
            aria-pressed={marked}
            aria-label={`${character}${marked ? " (erased)" : ""}`}
            onClick={(event) => {
              // A plain click is a tap, not a drag; the gesture layer has
              // already handled anything that involved movement.
              if (event.detail === 0) toggle(id);
            }}
            style={{
              ...textStyle.global,
              ...(marked ? textStyle.marked : textStyle.unmarked),
              padding: "12px",
              minWidth: "44px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "24px",
              // As with a word: the letter itself takes the drag, in any
              // direction, and never hands it back to the browser.
              touchAction: "none",
              background: "transparent",
            }}
          >
            {character}
          </button>
        ))}
      </div>
      <p
        style={{ marginBottom: 0, fontSize: "13px", color: "#555", textAlign: "center" }}
      >
        Drag across letters to erase them.
      </p>
    </Modal>
  );
};

export { CHARACTER_ID_ATTRIBUTE };
export default WordEditor;
